'use client';
import React, { useEffect, useMemo } from 'react';
import { useTranslations } from 'use-intl';
import JaycarHeader from '@/components/organisms/jaycar-header';
import mapJaycarCategories from '@/components/organisms/jaycar-header/utils/map-categories';
import AnnouncementBar from '@/components/organisms/announcement-bar';
import { resolveReference } from '@/utils/lib/resolve-reference';
import useMediaQuery from '@/hooks/useMediaQuery';
import useHideOnScroll from '@/hooks/useHideOnScroll';
import { desktop } from '@/constants/screensizes';
import { classnames } from '@/utils/classnames/classnames';
import { useSupportContact } from '@/providers/support-contact';
import { TasticProps } from '../types';
import { HeaderTasticProps } from './types';
import useHeaderData from './hooks/useHeaderData';
import { getHeaderSpacerHeight, getHeaderMobileHeight } from './utils/get-spacer-height';
import mapSecondaryLinks from './utils/map-secondary-links';
import resolveSupportContact from './utils/resolve-support-contact';
import type { NavItem } from '@/components/organisms/jaycar-header/types';

const HeaderTastic = ({ data }: TasticProps<HeaderTasticProps>) => {
  const translate = useTranslations();

  const {
    totalCartItems,
    navigationCategories,
    skuLookup,
    resolveSkus,
    backOrdersAllowed,
    paymentTerms,
    searchProducts,
    parseUploadFile,
    addToCart,
    csvShowProducts,
    csvShowProductsLoading,
    handleSKUsUpdate,
  } = useHeaderData();

  const productCategories = useMemo(() => mapJaycarCategories(navigationCategories), [navigationCategories]);

  // Studio-configured page links land in the secondary nav after the
  // hardcoded Shop Products trigger. The first NavItem is always
  // rendered as the slide-out menu trigger by JaycarHeader.
  const mainNavItems: NavItem[] = useMemo(
    () => [
      { label: translate('common.shop-products'), href: '/category', hasMenu: true },
      ...data.pageLinks.map((pl) => ({
        label: pl.name ?? '',
        href: resolveReference(pl.href).href ?? '/',
      })),
    ],
    [data.pageLinks, translate],
  );

  const secondaryLinks = useMemo(() => mapSecondaryLinks(data.secondaryLinks ?? []), [data.secondaryLinks]);

  const supportContact = useMemo(
    () =>
      resolveSupportContact({
        supportPhoneDisplay: data.supportPhoneDisplay,
        supportPhoneTel: data.supportPhoneTel,
        supportEmail: data.supportEmail,
      }),
    [data.supportPhoneDisplay, data.supportPhoneTel, data.supportEmail],
  );

  // Publish the resolved (localized) support contact so the globally-mounted
  // sign-in drawer reads the same Studio value. publish() is idempotent.
  const { publish } = useSupportContact();
  useEffect(() => {
    publish(supportContact);
  }, [publish, supportContact]);

  const accountHref = resolveReference(data.accountLink).href ?? '/account';
  const cartHref = resolveReference(data.cartLink).href ?? '/cart';
  const logoLink = resolveReference(data.logoLink);

  // EWM-551 — on mobile (< desktop breakpoint) the header hides on scroll-down
  // and reveals on scroll-up; desktop keeps it pinned. The outer spacer keeps
  // reserving the header's height so page content doesn't jump when it hides.
  // `offset` holds the header visible until the page has scrolled past its own
  // height, so hiding never exposes a white gap above the content.
  const [isDesktop] = useMediaQuery(desktop);
  const headerHidden = useHideOnScroll({
    enabled: !isDesktop,
    offset: getHeaderMobileHeight(data.variant, data.visibleBar),
  });

  return (
    <div className={getHeaderSpacerHeight(data.variant, data.visibleBar)}>
      <div
        id="header-container"
        className={classnames('fixed top-0 z-50 w-full transition-transform duration-300', {
          '-translate-y-full': headerHidden,
        })}
      >
        <div className="relative">
          {data.variant === 'navigation' && (
            <AnnouncementBar textBar={data.textBar} visibleBar={data.visibleBar} urlBar={data.urlBar} />
          )}
          <JaycarHeader
            logo={data.logo}
            logoLink={logoLink}
            mainNavItems={mainNavItems}
            productCategories={productCategories}
            cartItems={totalCartItems}
            accountHref={accountHref}
            cartHref={cartHref}
            onSkuLookup={skuLookup}
            resolveSkus={resolveSkus}
            backOrdersAllowed={backOrdersAllowed}
            paymentTerms={paymentTerms}
            searchProducts={searchProducts}
            parseUploadFile={parseUploadFile}
            addItem={addToCart}
            csvProducts={csvShowProducts}
            csvProductsLoading={csvShowProductsLoading}
            handleSKUsUpdate={handleSKUsUpdate}
            secondaryLinks={secondaryLinks}
            supportContact={supportContact}
          />
        </div>
      </div>
    </div>
  );
};
export default HeaderTastic;
